import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { authApi, setAccessToken } from '../lib/api';
import { User } from '../types';

interface FeatureFlags {
  download_codes_enabled: boolean;
  download_codes_start_date: string | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  featureFlags: FeatureFlags;
  authReady: boolean;            // true once initial me() has resolved (success or known-failure)
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, role?: string) => Promise<any>;
  logout: () => void;
  loading: boolean;
}

const DEFAULT_FLAGS: FeatureFlags = { download_codes_enabled: false, download_codes_start_date: null };

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Decide whether an axios error is a true "auth invalid" condition.
 * Only 401/403 should kick the user out — network errors, 5xx, and timeouts
 * are temporary and must NOT clear the session.
 */
function isHardAuthFailure(err: any): boolean {
  const status = err?.response?.status;
  return status === 401 || status === 403;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Hydrate on mount. The access token is NOT in localStorage — we re-establish
  // it from the httpOnly refresh cookie via /auth/refresh, then load the user.
  // `user` is a non-credential convenience copy that MAY still be cached for an
  // instant optimistic paint while we verify.
  useEffect(() => {
    let cancelled = false;
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try { setUser(JSON.parse(storedUser)); } catch { /* corrupted — ignore */ }
    }

    const hydrate = async () => {
      try {
        // Re-establish the in-memory access token from the refresh cookie.
        // Race a 10s timeout so a cold-starting backend never freezes loading.
        const timeoutGuard = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('auth-timeout')), 10000)
        );
        const refreshRes: any = await Promise.race([authApi.refresh(), timeoutGuard]);
        const newToken = refreshRes?.data?.data?.token;
        if (!newToken) {
          // No cookie session → treat as a hard auth failure below.
          throw Object.assign(new Error('no-session'), { response: { status: 401 } });
        }
        setAccessToken(newToken);
        if (!cancelled) setToken(newToken);

        // Load fresh user + feature flags now that we hold a token.
        const meRes: any = await authApi.me();
        const data = meRes.data?.data || {};
        const freshUser = data.user || meRes.data?.user;
        if (data.feature_flags && !cancelled) setFeatureFlags(data.feature_flags);
        if (freshUser && freshUser.id && !cancelled) {
          const merged = storedUser ? { ...JSON.parse(storedUser), ...freshUser } : freshUser;
          setUser(merged);
          localStorage.setItem('user', JSON.stringify(merged));
        }
      } catch (err) {
        // Hard auth failure (401/403 / no cookie) → logged out, clear everything.
        // Transient errors (network, 5xx, timeout) must NOT log the user out — keep
        // the optimistic cached user; the api 401-interceptor will re-refresh the
        // token on the first real API call once the backend is reachable.
        if (isHardAuthFailure(err)) {
          setAccessToken(null);
          localStorage.removeItem('user');
          if (!cancelled) { setToken(null); setUser(null); }
        }
      } finally {
        if (!cancelled) { setLoading(false); setAuthReady(true); }
      }
    };
    hydrate();
    return () => { cancelled = true; };
  }, []);

  // Listen for the api interceptor's soft "auth expired" event and clear state cleanly
  // (instead of the old hard window.location.href reload).
  useEffect(() => {
    const handler = () => {
      setAccessToken(null);
      setToken(null);
      setUser(null);
    };
    window.addEventListener('orbit:auth-expired', handler);
    return () => window.removeEventListener('orbit:auth-expired', handler);
  }, []);

  // Sliding-window token refresh — every 30 min while user is logged in.
  // Calls /api/auth/refresh which returns a fresh JWT (extends expiry).
  // This way, an active user never gets booted at the 7-day hard cap.
  useEffect(() => {
    if (!token) return;
    const tick = async () => {
      try {
        const res = await authApi.refresh();
        const newToken = res.data?.data?.token;
        if (newToken) {
          setAccessToken(newToken);   // in-memory only — never localStorage
          setToken(newToken);
        }
      } catch (err) {
        if (isHardAuthFailure(err)) {
          // Server says token is invalid — log out cleanly
          logout();
        }
        // else: transient error, leave existing token alone
      }
    };
    refreshTimerRef.current = setInterval(tick, 30 * 60 * 1000); // 30 min
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (email: string, password: string) => {
    const response = await authApi.login({ email, password });
    // Backend sets httpOnly access/refresh + readable csrf cookies AND returns the
    // access token in the body. We hold the token in memory only; `user` is a
    // non-credential copy that may be cached for optimistic paint on reload.
    const { user, token } = response.data.data;
    setAccessToken(token);
    localStorage.setItem('user', JSON.stringify(user));
    setToken(token);
    setUser(user);
    setAuthReady(true);
  };

  const register = async (name: string, email: string, password: string, role: string = 'creative') => {
    const response = await authApi.register({ name, email, password, role });
    if (response.data.requiresApproval) {
      return { requiresApproval: true, message: response.data.message };
    }
    // If an approved flow ever returns a token immediately, honor it (in-memory).
    const { user, token } = response.data;
    if (token) setAccessToken(token);
    if (user) localStorage.setItem('user', JSON.stringify(user));
    setToken(token || null);
    setUser(user || null);
    setAuthReady(true);
    return { requiresApproval: false };
  };

  const logout = async () => {
    // Revoke the refresh token + clear cookies server-side, then clear locally.
    try { await authApi.logout(); } catch { /* best-effort — clear locally regardless */ }
    setAccessToken(null);
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, featureFlags, authReady, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/** Check if a role has admin-level access (admin, creative_head, ATL, ceo, head_media_buying) */
export function isAdminRole(role?: string, additionalRoles?: string[]): boolean {
  const allRoles = [role, ...(additionalRoles || [])];
  return allRoles.some(r => ['admin', 'ceo', 'head_media_buying', 'creative_head', 'assistant_team_lead'].includes(r as string));
}
